import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import clsx from 'clsx';
import moment from 'moment-timezone';
import Icon from 'ziphy-web-shared/basic/assets/images/Icon';
import { WithLoaderWrapper } from 'ziphy-web-shared/basic/lib/loaders';
import { $calendar } from '@nedoShared/modules/calendar/Calendar.store';
import styles from './DashboardDayPicker.module.scss';
const weekdays = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const DashboardDayPicker = () => {
    const today = $calendar.timezone
        ? moment.tz($calendar.timezone).startOf('day')
        : moment().startOf('day');
    const [currentMonth, setCurrentMonth] = useState(today.clone().startOf('month'));
    const startOfMonth = currentMonth.clone().startOf('month');
    const endOfMonth = currentMonth.clone().endOf('month');
    const startDate = startOfMonth.clone().startOf('week');
    const endDate = endOfMonth.clone().endOf('week');
    useEffect(() => {
        const begin = startOfMonth
            .clone()
            .utc()
            .startOf('month')
            .startOf('week')
            .startOf('minute')
            .set('minute', 0)
            .set('second', 0)
            .set('millisecond', 0);
        const end = endOfMonth
            .clone()
            .utc()
            .endOf('month')
            .endOf('week')
            .endOf('minute')
            .set('minute', 0)
            .set('second', 0)
            .set('millisecond', 0);
        if ($calendar.selectedDay) {
            const selectedDayMoment = moment($calendar.selectedDay);
            if (!selectedDayMoment.isSame(currentMonth, 'month')) {
                let newSelectedDay = today.isSame(currentMonth, 'month')
                    ? today.clone()
                    : startOfMonth.clone();
                if (newSelectedDay.isBefore(today, 'day')) {
                    newSelectedDay = today.isSame(currentMonth, 'month')
                        ? today.clone()
                        : startOfMonth.clone();
                }
                const dayStr = newSelectedDay.format('YYYY-MM-DD');
                $calendar.SET_SELECTED_DAY(dayStr);
                let rangeBegin, rangeEnd;
                if ($calendar.isMobile || $calendar.isDay) {
                    rangeBegin = newSelectedDay.clone().startOf('day');
                    rangeEnd = rangeBegin.clone().add(1, 'day');
                }
                else {
                    rangeBegin = newSelectedDay.clone().startOf($calendar.weekType);
                    rangeEnd = rangeBegin.clone().add(1, 'week');
                }
                $calendar.SET_RANGE({
                    _begin: rangeBegin,
                    begin: rangeBegin,
                    _end: rangeEnd,
                    end: rangeEnd,
                });
            }
        }
        $calendar.SET_LOAD_RANGE({ _begin: begin, _end: end });
        $calendar.loadData(false);
    }, [currentMonth.valueOf()]);
    const dayIterator = startDate.clone().subtract(1, 'day');
    const calendar = [];
    while (dayIterator.isBefore(endDate, 'day')) {
        calendar.push(Array(7)
            .fill(0)
            .map(() => dayIterator.add(1, 'day').clone()));
    }
    const hasShifts = (d) => {
        var _a;
        const dateKey = d.format('YYYY-MM-DD');
        const daySlots = (_a = $calendar.slots) === null || _a === void 0 ? void 0 : _a[dateKey];
        if (!daySlots || !daySlots.length)
            return false;
        return daySlots.some((s) => { var _a; return (((_a = s === null || s === void 0 ? void 0 : s.shiftIds) === null || _a === void 0 ? void 0 : _a.length) || 0) > 0; });
    };
    const isDisabled = (d) => d.isBefore(today, 'day') || d.isAfter(today.clone().add(3, 'month'), 'day') || !hasShifts(d);
    const handleDayClick = (d) => {
        if (isDisabled(d))
            return;
        const dayStr = d.format('YYYY-MM-DD');
        $calendar.SET_SELECTED_DAY(dayStr);
        if (!d.isSame(currentMonth, 'month')) {
            setCurrentMonth(d.clone().startOf('month'));
            let begin, end;
            if ($calendar.isMobile || $calendar.isDay) {
                begin = d.clone().startOf('day');
                end = begin.clone().add(1, 'day');
            }
            else {
                begin = d.clone().startOf($calendar.weekType);
                end = begin.clone().add(1, 'week');
            }
            $calendar.SET_RANGE({
                _begin: begin,
                begin: begin,
                _end: end,
                end: end,
            });
            return;
        }
        let begin, end;
        if ($calendar.isMobile || $calendar.isDay) {
            begin = d.clone().startOf('day');
            end = begin.clone().add(1, 'day');
        }
        else {
            begin = d.clone().startOf($calendar.weekType);
            end = begin.clone().add(1, 'week');
        }
        $calendar.SET_RANGE({
            _begin: begin,
            begin: begin,
            _end: end,
            end: end,
        });
    };
    const disabledBack = () => {
        return currentMonth.isSameOrBefore(today, 'month');
    };
    const disabledNext = () => {
        return currentMonth.isSameOrAfter(today.clone().add(3, 'month'), 'month');
    };
    return (_jsxs("div", Object.assign({ className: styles.calendar }, { children: [_jsxs("div", Object.assign({ className: styles.calendarHeader }, { children: [_jsx("button", Object.assign({ disabled: disabledBack(), onClick: () => setCurrentMonth(currentMonth.clone().subtract(1, 'month')) }, { children: _jsx(Icon, { type: "back" }) })), _jsx("span", Object.assign({ className: styles.calendarMonth }, { children: currentMonth.format('MMMM YYYY').toUpperCase() })), _jsx("button", Object.assign({ disabled: disabledNext(), onClick: () => setCurrentMonth(currentMonth.clone().add(1, 'month')) }, { children: _jsx(Icon, { type: "next" }) }))] })), _jsx("div", Object.assign({ className: styles.calendarWeekdays }, { children: weekdays.map((d) => (_jsx("div", Object.assign({ className: styles.calendarWeekday }, { children: d }), d))) })), _jsx(WithLoaderWrapper, Object.assign({ withWrapper: false, visibleCondition: true, isLoading: $calendar.isLoading }, { children: _jsx("div", Object.assign({ className: styles.calendarDays }, { children: calendar.map((week, wi) => (_jsx("div", Object.assign({ className: styles.calendarWeek }, { children: week.map((d) => {
                            const isToday = d.isSame(today, 'day');
                            const isSelected = $calendar.selectedDay && moment($calendar.selectedDay).isSame(d, 'day');
                            return (_jsx("div", Object.assign({ className: clsx(styles.calendarDay, {
                                    [styles.today]: isToday,
                                    [styles.selected]: isSelected,
                                    [styles.disabled]: isDisabled(d),
                                }), onClick: () => handleDayClick(d) }, { children: _jsx("span", { children: d.date() }) }), d.toString()));
                        }) }), wi))) })) }))] })));
};
export default observer(DashboardDayPicker);
